import React, { KeyboardEvent, PropsWithChildren, ReactChild, ReactNode, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FlowConfigContext } from '../../../providers/FlowConfigContext';
import SVGCivicPassLogo from '../../assets/svg/SVGCivicPassLogo';
import WindowProvider from '../../window';
import { useTheme } from '../../../providers/theme/ThemeProvider';

interface ChildProps {
  children: ReactChild | ReactChild[];
  id?: string;
  iframeContainerCssClass?: string;
  mainContainerCssClass?: string;
  HeaderComponent?: ReactNode;
  verticalPadding?: string;
}

export default function Container({
  children,
  iframeContainerCssClass,
  mainContainerCssClass,
  HeaderComponent,
  id,
  verticalPadding,
}: PropsWithChildren<ChildProps>): React.ReactElement {
  const { networkName, getExternalLinksConfig } = useContext(FlowConfigContext);
  const { darkModeSupported } = useTheme();
  const { civicWebsiteLink } = getExternalLinksConfig();
  const { t } = useTranslation();
  const [viewContainerClass, setViewContainerClass] = useState<string>('');

  useEffect(() => {
    let className = `iframe-view-container bg-background max-w-xl ${networkName}-container ${iframeContainerCssClass}`;
    if (!WindowProvider.isInIframe()) {
      className += ' notIframe';
    }
    // Add padding top for dark mode since that padding is not shown anymore by RC/gateway-client versions that support dark mode
    if (darkModeSupported) {
      className += ' pt-6';
    }
    setViewContainerClass(className);
  }, [networkName]);

  const handleCivicLogoClick = () => {
    window.open(civicWebsiteLink, '_blank');
  };

  const handleCivicLogoKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    if (event.key === 'Enter') {
      handleCivicLogoClick();
    }
  };

  return (
    <div data-testid="IFRAME_VIEW_CONTAINER" id={id} data-iframe-size className={viewContainerClass}>
      {HeaderComponent && <header className="mt-5">{HeaderComponent}</header>}
      <div
        className={`mainContainer full:px-10 wider:px-10 wide:px-10 medium:px-6 narrow:px-6 justify-between flex-none relative ${mainContainerCssClass}`}
      >
        <div
          id="scrollContainer"
          className={`content-container flex flex-col justify-center items-center flex-grow-0 flex-shrink-0 ${verticalPadding}`}
        >
          {children}
        </div>
      </div>
      <footer className="py-4 flex flex-row justify-center items-center">
        <div
          className="flex"
          role="button"
          onClick={handleCivicLogoClick}
          onKeyDown={handleCivicLogoKeyDown}
          tabIndex={0}
        >
          <span className="textFooter text-textTertiary mr-1 mt-px">{t('common.poweredBy')} </span>
          <SVGCivicPassLogo />
        </div>
      </footer>
    </div>
  );
}

Container.defaultProps = {
  iframeContainerCssClass: '',
  mainContainerCssClass: '',
  HeaderComponent: null,
  id: 'iframeViewContainer',
  verticalPadding: 'py-4',
};
